import { API_BASE_URL } from '../config';

export interface ImportConflictResolution {
  name: string;
  action: 'create_new' | 'use_existing' | 'skip';
  new_name?: string;
  parent_ref?: string;
}

export interface ImportStatePreview {
  name: string;
  is_initial: boolean;
  is_final: boolean;
  conflict_type: 'new' | 'duplicate';
}

export interface ImportProcessPreview {
  name: string;
  process_type?: string;
  conflict_type: 'new' | 'duplicate';
}

export interface ImportTransitionPreview {
  name: string;
  from_state?: string;
  to_state?: string;
  process_ref?: string;
  conflict_type: 'new' | 'duplicate';
}

export interface ImportArtifactPreview {
  name: string;
  description?: string;
  parent_ref?: string;
  conflict_type: 'new' | 'duplicate';
  states: ImportStatePreview[];
  processes: ImportProcessPreview[];
  transitions: ImportTransitionPreview[];
}

export interface ImportPreview {
  total_artifacts: number;
  new_artifacts: number;
  conflicts: number;
  artifacts: ImportArtifactPreview[];
}

export interface PrimeArtifactImportRequest {
  json_data: any[];
  conflict_resolutions: ImportConflictResolution[];
  target_root_id?: string;
}

export interface PrimeArtifactImportResponse {
  success: boolean;
  created_artifacts: number;
  created_states: number;
  created_processes: number;
  created_transitions: number;
  message: string;
  errors?: string[];
}

class PrimeArtifactImportService {
  private baseUrl = `${API_BASE_URL}/prime-artifacts/import`;

  async previewImport(file: File, targetRootId?: string): Promise<ImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    if (targetRootId) {
      formData.append('target_root_id', targetRootId);
    }

    const response = await fetch(`${this.baseUrl}/preview`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to preview import');
    }

    return response.json();
  }

  async executeImport(request: PrimeArtifactImportRequest): Promise<PrimeArtifactImportResponse> {
    const response = await fetch(`${this.baseUrl}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to execute import');
    }

    return response.json();
  }
}

export const primeArtifactImportService = new PrimeArtifactImportService();
